import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { cn, LockIcon, ModuleHeader, SpinnerWithBackground } from '@viapeople/components'
import type * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { trpc } from '../../utils/trpc'
import { useCycleAdminAccess } from '../../utils/useCycleAdminAccess'

// Cloned from apps/admin/src/modules/cycles/tabs/HrReviewFlagsTab.tsx so HR
// admins who primarily use the client app can triage flags without context-
// switching into admin. Same UX, same dual gate (feature flag +
// cycle-admin membership), same color tokens. Differences vs. admin:
//   - No URL org segment; we read organizationId via `client.hrReviewFlags.context`.
//   - "View Full Evaluation" navigates inside the client app to the native
//     cycle-execution route — no inline read-only modal needed.
//   - tRPC routers / sidebar invalidation key are `client.*` instead of `admin.*`.

type HrReviewFlagSearchRow = inferRouterOutputs<AppRouter>['client']['hrReviewFlags']['search']['data'][number]

type FlagStatus = 'pending' | 'in_review' | 'resolved'

// Color tokens from the design artifact (remixed-21c5b182.tsx). The project's
// neutral palette renders borders darker than the mock — using the artifact's
// exact hex keeps cards and dividers visually correct.
const C = {
	bg: '#f8f9fb',
	surface: '#ffffff',
	border: '#e4e7ec',
	borderLight: '#f2f4f7',
	text: '#0f1729',
	textSec: '#364152',
	textMuted: '#637083',
	textDim: '#9aa4b2',
	accent: '#4a6cf7',
	accentBg: '#eef1fe',
	accentBorder: '#c5d0f9',
	green: '#16a34a',
	greenBg: '#f0fdf4',
	greenBorder: '#bbf7d0',
	amber: '#d97706',
	amberBg: '#fffbeb',
} as const

const STATUS_LABELS: Record<FlagStatus, string> = {
	pending: 'Pending',
	in_review: 'In Review',
	resolved: 'Resolved',
}

const STATUS_COLORS: Record<FlagStatus, { dot: string; pillBg: string; pillText: string }> = {
	pending: { dot: C.amber, pillBg: C.amberBg, pillText: C.amber },
	in_review: { dot: C.accent, pillBg: C.accentBg, pillText: C.accent },
	resolved: { dot: C.green, pillBg: C.greenBg, pillText: C.green },
}

const FILTER_TABS: Array<{ id: 'all' | FlagStatus; label: string }> = [
	{ id: 'all', label: 'All' },
	{ id: 'pending', label: 'Pending' },
	{ id: 'in_review', label: 'In Review' },
	{ id: 'resolved', label: 'Resolved' },
]

const formatDate = (value: Date | string): string => {
	const date = typeof value === 'string' ? new Date(value) : value
	return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const readCustom = (customFields: unknown, key: string): string => {
	if (!customFields || typeof customFields !== 'object') return ''
	const value = (customFields as Record<string, unknown>)[key]
	return typeof value === 'string' ? value : ''
}

export function HrReviewFlagsPage() {
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	// Defense-in-depth: server enforces the same gates via the sidebar query
	// + service-level CA check, but a non-CA who guesses the URL would
	// otherwise see an empty queue with no explanation. Render a friendly
	// "not enabled" state when either gate fails.
	const { isEnabled: hrFlagsEnabled, isLoading: flagLoading } = useFeatureFlag('hr-review-flags')
	const { hasAccess: hasCaAccess, isLoading: caLoading } = useCycleAdminAccess()

	const orgContextQuery = useQuery({
		...trpc.client.hrReviewFlags.context.queryOptions(),
		enabled: hrFlagsEnabled && hasCaAccess,
	})
	const organizationId = orgContextQuery.data?.organizationId

	const [activeFilter, setActiveFilter] = useState<'all' | FlagStatus>('all')
	const [expandedId, setExpandedId] = useState<string | null>(null)

	// Pagination state. The search route caps `perPage` at 200; for orgs
	// over that ceiling we accumulate pages here and surface a "Load more"
	// footer so the list never silently truncates relative to the badge
	// counts (which come from countByStatus and reflect the full org).
	const PER_PAGE = 200
	const [page, setPage] = useState(1)
	const [accumulated, setAccumulated] = useState<HrReviewFlagSearchRow[]>([])

	// Reset accumulation whenever the filter changes — different status =
	// different result set, so previous pages are no longer the head.
	// biome-ignore lint/correctness/useExhaustiveDependencies: activeFilter is the trigger for this reset; the body only resets state and doesn't read the dep
	useEffect(() => {
		setPage(1)
		setAccumulated([])
	}, [activeFilter])

	const flagsQuery = useQuery({
		...trpc.client.hrReviewFlags.search.queryOptions({
			organizationId: organizationId as string,
			status: activeFilter === 'all' ? undefined : activeFilter,
			page,
			perPage: PER_PAGE,
		}),
		enabled: !!organizationId && hrFlagsEnabled && hasCaAccess,
	})

	// Append fresh page rows to the accumulator. On page=1 we replace the
	// list (filter just changed or first load); subsequent pages append.
	useEffect(() => {
		const rows = flagsQuery.data?.data
		if (!rows) return
		setAccumulated((prev) => (page === 1 ? rows : [...prev, ...rows]))
	}, [flagsQuery.data, page])

	const countByStatusQuery = useQuery({
		...trpc.client.hrReviewFlags.countByStatus.queryOptions({ organizationId: organizationId as string }),
		enabled: !!organizationId && hrFlagsEnabled && hasCaAccess,
	})

	const invalidateAll = () => {
		queryClient.invalidateQueries({ queryKey: trpc.client.hrReviewFlags.search.queryKey(), exact: false })
		queryClient.invalidateQueries({ queryKey: trpc.client.hrReviewFlags.countByStatus.queryKey(), exact: false })
		queryClient.invalidateQueries({ queryKey: trpc.client.hrReviewFlags.countPending.queryKey(), exact: false })
		queryClient.invalidateQueries({ queryKey: trpc.client.sidebar.initialize.queryKey(), exact: false })
	}

	const updateStatusMutation = useMutation({
		...trpc.client.hrReviewFlags.updateStatus.mutationOptions(),
		onSuccess: invalidateAll,
	})

	const updateNotesMutation = useMutation({
		...trpc.client.hrReviewFlags.updateInternalNotes.mutationOptions(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: trpc.client.hrReviewFlags.search.queryKey(), exact: false })
		},
	})

	const counts = useMemo(() => {
		const summary = countByStatusQuery.data ?? { pending: 0, in_review: 0, resolved: 0 }
		const total = summary.pending + summary.in_review + summary.resolved
		return {
			all: total,
			pending: summary.pending,
			in_review: summary.in_review,
			resolved: summary.resolved,
		} satisfies Record<FlagStatus | 'all', number>
	}, [countByStatusQuery.data])

	// Render from the accumulator so previously-loaded pages remain visible
	// while a new page is fetching. `flags.length` is the running visible
	// count; `total` is the full filter-respecting count from the server.
	const flags = accumulated
	const total = flagsQuery.data?.meta?.total ?? 0
	const hasMore = total > flags.length
	const isFetchingNextPage = flagsQuery.isFetching && page > 1

	if (flagLoading || caLoading) {
		return <SpinnerWithBackground />
	}

	if (!hrFlagsEnabled || !hasCaAccess) {
		return (
			<main className='flex min-h-full flex-col p-6' style={{ background: C.bg }}>
				<div className='mx-auto w-full max-w-[780px] flex flex-col gap-4'>
					<ModuleHeader
						breadcrumb={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'HR Review Flags' }]}
						title='HR Review Flags'
					/>
					<div
						className='rounded-xl px-6 py-12 text-center'
						style={{ background: C.surface, border: `1px solid ${C.border}` }}>
						<span className='text-[12px]' style={{ color: C.textMuted }}>
							HR Review Flags is not available for your account.
						</span>
					</div>
				</div>
			</main>
		)
	}

	if ((flagsQuery.isLoading && flags.length === 0) || orgContextQuery.isLoading) {
		return <SpinnerWithBackground />
	}

	return (
		<main className='flex min-h-full flex-col p-6' style={{ background: C.bg }}>
			<div className='mx-auto flex w-full max-w-[780px] flex-col gap-4'>
				<ModuleHeader
					breadcrumb={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'HR Review Flags' }]}
					title='HR Review Flags'
				/>
				<div className='flex items-center gap-1.5' style={{ color: C.textMuted }}>
					<span className='text-[11px]'>All cycles</span>
					<span className='text-[11px]' style={{ color: C.textDim }}>
						·
					</span>
					<span className='inline-flex items-center gap-1 text-[11px] font-semibold'>
						<LockIcon size={12} className='fill-current' />
						Confidential
					</span>
				</div>

				<div role='tablist' aria-label='Filter by status' className='flex flex-wrap gap-1'>
					{FILTER_TABS.map((tab) => {
						const isActive = activeFilter === tab.id
						return (
							<button
								key={tab.id}
								role='tab'
								aria-selected={isActive}
								type='button'
								onClick={() => setActiveFilter(tab.id)}
								className='inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[11px] font-semibold transition-colors'
								style={{
									border: `${isActive ? '1.5px' : '1px'} solid ${isActive ? C.accent : C.border}`,
									background: isActive ? C.accentBg : C.surface,
									color: isActive ? C.accent : C.textSec,
								}}>
								{tab.label}
								<span
									className='inline-flex items-center rounded-full px-1.5 py-px text-[9px] font-bold'
									style={{
										background: isActive ? `${C.accent}26` : C.bg,
										color: isActive ? C.accent : C.textDim,
									}}>
									{counts[tab.id]}
								</span>
							</button>
						)
					})}
				</div>

				<div className='flex flex-col gap-2'>
					{flags.length === 0 ? (
						<div
							className='rounded-xl px-6 py-12 text-center'
							style={{ background: C.surface, border: `1px solid ${C.border}` }}>
							<span className='text-[12px]' style={{ color: C.textMuted }}>
								No HR review flags in this view.
							</span>
						</div>
					) : (
						flags.map((flag) => {
							const status = flag.status as FlagStatus
							const colors = STATUS_COLORS[status]
							const isExpanded = expandedId === flag.id
							const employee = flag.employee
							const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown employee'
							const title = readCustom(flag.customFields, 'title')
							const department = readCustom(flag.customFields, 'department')
							const managerName =
								readCustom(flag.customFields, 'manager') || readCustom(flag.customFields, 'managerName')
							const reasonPreview = (flag.reasonText || '').replace(/\s+/g, ' ').trim()
							const evaluationHref = flag.selfTaskId
								? `/cycle/${flag.cycleId}/tasks/${flag.selfTaskId}/cycle-execution`
								: null

							return (
								<div
									key={flag.id}
									data-testid='hr-review-flag-card'
									className='overflow-hidden rounded-xl'
									style={{ background: C.surface, border: `1px solid ${C.border}` }}>
									<button
										type='button'
										className='flex w-full items-center gap-3 px-4 py-3 text-left'
										onClick={() => setExpandedId(isExpanded ? null : flag.id)}>
										<span
											aria-hidden='true'
											className='size-2 shrink-0 rounded-full'
											style={{ background: colors.dot }}
										/>
										<div className='flex min-w-0 flex-1 flex-col'>
											<div className='flex min-w-0 items-baseline gap-1.5'>
												<span className='truncate text-[13px] font-semibold' style={{ color: C.text }}>
													{employeeName}
												</span>
												<span className='truncate text-[9px]' style={{ color: C.textMuted }}>
													{[title, department].filter(Boolean).join(' · ') || ''}
												</span>
											</div>
											<span className='truncate text-[10px]' style={{ color: C.textMuted, maxWidth: 400 }}>
												{reasonPreview || '— No reason provided —'}
											</span>
										</div>
										<div className='flex shrink-0 flex-col items-end gap-0.5'>
											<span
												className='rounded px-2 py-0.5 text-[8px] font-bold uppercase tracking-wide'
												style={{ background: colors.pillBg, color: colors.pillText }}>
												{STATUS_LABELS[status]}
											</span>
											<span className='text-[9px]' style={{ color: C.textDim }}>
												{formatDate(flag.createdAt)}
											</span>
										</div>
										<svg
											width='12'
											height='12'
											viewBox='0 0 12 12'
											fill='none'
											className={cn('shrink-0 transition-transform', isExpanded && 'rotate-180')}
											aria-hidden='true'>
											<path d='M3 4.5l3 2.5 3-2.5' stroke={C.textMuted} strokeWidth='1.2' strokeLinecap='round' />
										</svg>
									</button>
									{isExpanded && (
										<div
											className='flex flex-col gap-3.5 px-4 py-3.5'
											style={{ background: C.bg, borderTop: `1px solid ${C.borderLight}` }}>
											<div className='flex flex-col gap-4 md:flex-row md:items-start'>
												<div className='flex w-full flex-col gap-1.5 text-left md:basis-2/5 md:shrink-0'>
													<span
														className='text-[10px] font-bold uppercase'
														style={{ color: C.textDim, letterSpacing: '0.5px' }}>
														Employee Details
													</span>
													<dl
														className='grid items-baseline text-[10px] text-left'
														style={{ gridTemplateColumns: '70px 1fr', rowGap: 3, columnGap: 8 }}>
														<dt style={{ color: C.textDim }}>Name</dt>
														<dd className='font-semibold' style={{ color: C.text }}>
															{employeeName}
														</dd>
														{title && (
															<>
																<dt style={{ color: C.textDim }}>Title</dt>
																<dd style={{ color: C.text }}>{title}</dd>
															</>
														)}
														{department && (
															<>
																<dt style={{ color: C.textDim }}>Department</dt>
																<dd style={{ color: C.text }}>{department}</dd>
															</>
														)}
														{managerName && (
															<>
																<dt style={{ color: C.textDim }}>Manager</dt>
																<dd style={{ color: C.text }}>{managerName}</dd>
															</>
														)}
														<dt style={{ color: C.textDim }}>Cycle</dt>
														<dd style={{ color: C.text }}>{flag.cycleName}</dd>
														<dt style={{ color: C.textDim }}>Flagged</dt>
														<dd style={{ color: C.text }}>{formatDate(flag.createdAt)}</dd>
													</dl>
													{evaluationHref && (
														<button
															type='button'
															onClick={() => navigate(evaluationHref)}
															className='mt-2 self-start rounded-md px-3 py-1 text-[9px] font-semibold transition-colors hover:opacity-80'
															style={{
																background: C.surface,
																border: `1px solid ${C.border}`,
																color: C.accent,
															}}>
															View Full Evaluation →
														</button>
													)}
												</div>
												<div className='flex w-full flex-col gap-1.5 md:basis-3/5 md:flex-1'>
													<span
														className='text-[10px] font-bold uppercase'
														style={{ color: C.textDim, letterSpacing: '0.5px' }}>
														Reason Provided
													</span>
													<div
														className='rounded-lg px-3 py-2.5'
														style={{
															background: C.surface,
															border: `1px solid ${C.border}`,
															color: C.textSec,
															fontSize: 11,
															lineHeight: 1.7,
															whiteSpace: 'pre-wrap',
														}}>
														{flag.reasonText || '— No reason provided —'}
													</div>
												</div>
											</div>

											<div
												className='flex flex-col gap-2 rounded-lg px-3.5 py-3'
												style={{ background: C.surface, border: `1px solid ${C.border}` }}>
												<span
													className='text-[10px] font-bold uppercase'
													style={{ color: C.textDim, letterSpacing: '0.5px' }}>
													HR Actions
												</span>
												<div className='flex flex-col gap-3 md:flex-row md:items-start'>
													<div className='flex w-full flex-col gap-1 md:flex-1'>
														<label
															className='text-[10px] font-semibold'
															style={{ color: C.text }}
															htmlFor={`hr-notes-${flag.id}`}>
															Internal Notes{' '}
															<span className='font-normal text-[9px]' style={{ color: C.textDim }}>
																(HR-only)
															</span>
														</label>
														<HrReviewFlagInternalNotes
															id={`hr-notes-${flag.id}`}
															key={flag.id}
															initialValue={flag.internalNotes ?? ''}
															onSave={async (value) => {
																await updateNotesMutation.mutateAsync({
																	id: flag.id,
																	cycleId: flag.cycleId,
																	internalNotes: value,
																})
															}}
														/>
													</div>
													<div className='flex w-full flex-col gap-1 md:w-[140px] md:shrink-0'>
														<span className='text-[10px] font-semibold' style={{ color: C.text }}>
															Status
														</span>
														<div className='flex flex-col gap-1'>
															{status === 'pending' && (
																<>
																	<StatusActionButton
																		bg={C.accentBg}
																		border={C.accentBorder}
																		color={C.accent}
																		onClick={() =>
																			updateStatusMutation.mutate({
																				id: flag.id,
																				cycleId: flag.cycleId,
																				status: 'in_review',
																			})
																		}>
																		Mark In Review
																	</StatusActionButton>
																	<StatusActionButton
																		bg={C.greenBg}
																		border={C.greenBorder}
																		color={C.green}
																		onClick={() =>
																			updateStatusMutation.mutate({
																				id: flag.id,
																				cycleId: flag.cycleId,
																				status: 'resolved',
																			})
																		}>
																		✓ Mark Resolved
																	</StatusActionButton>
																</>
															)}
															{status === 'in_review' && (
																<StatusActionButton
																	bg={C.greenBg}
																	border={C.greenBorder}
																	color={C.green}
																	onClick={() =>
																		updateStatusMutation.mutate({
																			id: flag.id,
																			cycleId: flag.cycleId,
																			status: 'resolved',
																		})
																	}>
																	✓ Mark Resolved
																</StatusActionButton>
															)}
															{status === 'resolved' && (
																<StatusActionButton
																	bg={C.bg}
																	border={C.border}
																	color={C.textMuted}
																	onClick={() =>
																		updateStatusMutation.mutate({
																			id: flag.id,
																			cycleId: flag.cycleId,
																			status: 'pending',
																		})
																	}>
																	↩ Reopen
																</StatusActionButton>
															)}
														</div>
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
							)
						})
					)}
					{hasMore && (
						<div
							className='mt-1 flex items-center justify-between rounded-md px-3 py-2'
							style={{ background: C.surface, border: `1px solid ${C.border}` }}>
							<span className='text-[11px]' style={{ color: C.textMuted }}>
								Showing {flags.length} of {total}
							</span>
							<button
								type='button'
								onClick={() => setPage((prev) => prev + 1)}
								disabled={isFetchingNextPage}
								className='rounded-md px-3 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50'
								style={{
									background: C.surface,
									border: `1px solid ${C.border}`,
									color: C.accent,
								}}>
								{isFetchingNextPage ? 'Loading…' : 'Load more'}
							</button>
						</div>
					)}
				</div>
			</div>
		</main>
	)
}

interface StatusActionButtonProps {
	bg: string
	border: string
	color: string
	onClick: () => void
	children: React.ReactNode
}

function StatusActionButton({ bg, border, color, onClick, children }: StatusActionButtonProps) {
	return (
		<button
			type='button'
			onClick={onClick}
			className='rounded-md px-3 py-1.5 text-left text-[10px] font-semibold transition-colors hover:opacity-90'
			style={{ background: bg, border: `1px solid ${border}`, color }}>
			{children}
		</button>
	)
}

interface HrReviewFlagInternalNotesProps {
	id?: string
	initialValue: string
	onSave: (value: string) => Promise<void>
}

function HrReviewFlagInternalNotes({ id, initialValue, onSave }: HrReviewFlagInternalNotesProps) {
	const [value, setValue] = useState(initialValue)
	const [savedValue, setSavedValue] = useState(initialValue)
	const [saving, setSaving] = useState(false)
	const [saveError, setSaveError] = useState<string | null>(null)

	const persist = async (next: string) => {
		setSaving(true)
		setSaveError(null)
		try {
			await onSave(next)
			setSavedValue(next)
		} catch (err) {
			console.error('HR review flag notes save failed', err)
			setSaveError(err instanceof Error ? err.message : 'Could not save notes. Please try again.')
		} finally {
			setSaving(false)
		}
	}

	const handleBlur = () => {
		if (value === savedValue) return
		void persist(value)
	}

	return (
		<div className='flex flex-col gap-1'>
			<textarea
				id={id}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onBlur={handleBlur}
				placeholder='Add notes about conversations, next steps, or resolution…'
				rows={2}
				maxLength={5000}
				data-testid='hr-review-flag-notes'
				className='w-full resize-y rounded-md px-2.5 py-2 text-[10px] outline-none focus:border-blue-300'
				style={{
					background: C.bg,
					border: `1px solid ${C.border}`,
					color: C.textSec,
					minHeight: 60,
					lineHeight: 1.5,
				}}
			/>
			{saving && (
				<span className='text-[9px]' style={{ color: C.textDim }}>
					Saving…
				</span>
			)}
			{!saving && saveError && (
				<div className='flex items-center justify-between gap-2'>
					<span className='text-[9px] text-red-600'>{saveError}</span>
					<button
						type='button'
						onClick={() => void persist(value)}
						disabled={saving}
						className='rounded px-2 py-0.5 text-[9px] font-semibold'
						style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.accent }}>
						Retry save
					</button>
				</div>
			)}
		</div>
	)
}
